import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export type ContainerFileDropChoice = "workspace" | "copy";

/**
 * The interception for moving a canvas/chat file into another container
 * (D1/A3): the routine path promotes the file to the workspace and adds a live
 * reference here; the explicit secondary makes an independent copy.
 */
export function ContainerFileDropDialog({
  count,
  fileName,
  destination,
  onChoose,
  onClose,
}: {
  count: number;
  /** Shown when count === 1. */
  fileName?: string;
  destination: "canvas" | "chat";
  onChoose: (choice: ContainerFileDropChoice) => void;
  onClose: () => void;
}) {
  const what = count === 1 ? (fileName ?? "This file") : `These ${count} files`;
  const verb = destination === "chat" ? "attach" : "add";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="container-file-drop">
        <DialogHeader>
          <DialogTitle>
            {count === 1
              ? `Add "${fileName}" here?`
              : `Add ${count} files here?`}
          </DialogTitle>
          <DialogDescription>
            {what} {count === 1 ? "lives" : "live"} on another{" "}
            {count === 1 ? "canvas or chat" : "canvas or chat"}. Adding to the
            workspace keeps one live file everyone sees — this {destination}{" "}
            gets a reference, and the original stays where it is. A copy is
            independent: edits here won&apos;t show up anywhere else.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => onChoose("copy")}>
            {count === 1 ? "Add a copy instead" : "Add copies instead"}
          </Button>
          <Button onClick={() => onChoose("workspace")}>
            Add to workspace &amp; {verb} here
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
